import { Component, ElementRef, inject, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { EmailNotificationsService } from '../../service/EmailNotificationsService';
import { AuthService } from '../../service/auth.service';

@Component({
    selector: 'app-email-notifications-list',
    templateUrl: './email-notifications-list.component.html',
    styleUrls: ['./email-notifications-list.component.css']
})
export class EmailNotificationsListComponent implements OnInit, OnDestroy {
    emailNotificationsService = inject(EmailNotificationsService);
    authService = inject(AuthService);
    router = inject(Router);
    @ViewChild('rangeDialog') private rangeDialog?: ElementRef<HTMLDialogElement>;
    @ViewChild('rangeButton') private rangeButton?: ElementRef<HTMLButtonElement>;
    isDateRangePickerOpened = false;
    draftStart = '';
    draftEnd = '';
    draftRangeError = '';

    get maxDateTime(): string {
        return this.localDateTime(new Date());
    }

    openDateRangePicker(): void {
        this.draftStart = this.localDateTime(new Date(this.emailNotificationsService.startDate));
        this.draftEnd = this.localDateTime(new Date(this.emailNotificationsService.endDate));
        this.draftRangeError = '';
        const dialog = this.rangeDialog?.nativeElement;
        if (!dialog || dialog.open) return;
        const button = this.rangeButton?.nativeElement.getBoundingClientRect();
        dialog.style.top = Math.max(16, Math.min((button?.bottom || 100) + 10, window.innerHeight - 360)) + 'px';
        dialog.showModal();
        this.isDateRangePickerOpened = true;
    }

    closeDateRangePicker(): void {
        this.rangeDialog?.nativeElement.close();
        this.onDateRangePickerClosed();
    }

    onDateRangePickerClosed(): void {
        this.isDateRangePickerOpened = false;
        this.draftRangeError = '';
    }

    onDateRangeBackdropClick(event: MouseEvent): void {
        const dialog = this.rangeDialog?.nativeElement;
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right ||
            event.clientY < rect.top || event.clientY > rect.bottom) this.closeDateRangePicker();
    }

    applyDateRange(): void {
        const start = new Date(this.draftStart).getTime();
        const end = new Date(this.draftEnd).getTime();
        if (!this.draftStart || !this.draftEnd || !Number.isFinite(start) || !Number.isFinite(end)) {
            this.draftRangeError = 'Choose both a start and end date.';
            return;
        }
        if (start > end) {
            this.draftRangeError = 'End date must be on or after start date.';
            return;
        }
        if (end > Date.now()) {
            this.draftRangeError = 'Choose a date and time that is not in the future.';
            return;
        }
        this.emailNotificationsService.submitEmailFormDate({
            valueOf: () => ({startDate: this.draftStart, endDate: this.draftEnd})
        });
        this.closeDateRangePicker();
    }

    private localDateTime(date: Date): string {
        const pad = (value: number) => String(value).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    ngOnInit() {
        this.getTodayEmailNotifications();
        this.emailNotificationsService.getEmailNotifications();
    }

    ngOnDestroy() {
        if (this.isDateRangePickerOpened) this.closeDateRangePicker();
        localStorage.removeItem('emailStartDate');
        localStorage.removeItem('emailEndDate');
        this.emailNotificationsService.resetEmailNotifications();
        this.emailNotificationsService.selectedTimeFrame.set('');
    }

    openCustomerEmailDetails(customerNumber: number, email: string) {
        this.router.navigate(['/app/email-notifications/customer-details'], {
            queryParams: {
                customerNumber: customerNumber,
                email: email,
                startDate: this.emailNotificationsService.startDate,
                endDate: this.emailNotificationsService.endDate
            }
        });
    }

    getTodayEmailNotifications() {
        this.emailNotificationsService.endDate = new Date().toISOString();

        const d = new Date();
        d.setUTCHours(0, 0, 0, 0);
        this.emailNotificationsService.startDate = d.toISOString();

        localStorage.setItem('emailStartDate', this.emailNotificationsService.startDate);
        localStorage.setItem('emailEndDate', this.emailNotificationsService.endDate);

        this.emailNotificationsService.selectedTimeFrame.set('Today');
    }


    onScroll() {
        this.emailNotificationsService.increasePageNumber();
        this.emailNotificationsService.getEmailNotificationsOnScroll();
    }

    refreshTable() {
        if (this.isDateRangePickerOpened) this.closeDateRangePicker();
        this.emailNotificationsService.refreshTableOnInit();
    }

    hasPermission() {
        return this.authService.isSuperAdmin();
    }
}